import BillingMethod from 'billing/models/BillingMethod'
import { CancellationToken } from 'infra/cancellation'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { Observable, Subject } from 'rxjs'
import { cancelOnDeactivate } from 'utils/rx-util'

/**
 * On Save delegate.
 */
export type OnSaveDelegate = (method: BillingMethod) => Promise<unknown>

/**
 * Payment method selector view model.
 */
export default class PaymentMethodSelectorViewModel {
  /**
   * Subject used to emit when a method is selected.
   *
   * @private
   */
  private readonly onPaymentMethodSelected = new Subject<BillingMethod>()

  /**
   * Emits when a payment method is selected.
   */
  readonly onPaymentMethodSelected$ =
    this.onPaymentMethodSelected.asObservable()

  /**
   * The selected payment method, if any.
   */
  @observable selectedPaymentMethod: BillingMethod | null = null

  /**
   * Constructor,
   *
   * @param adapter
   */
  constructor(private adapter: PaymentMethodSelectorViewModelPorts) {
    makeObservable(this)
  }

  /**
   * Whether the controls for this editor are disabled.
   */
  @computed
  get disabled() {
    return this.isLoading
  }

  /**
   * Whether the billing info is loading.
   */
  @computed
  get isLoading() {
    return this.prepare.pending
  }

  /**
   * The available billing methods.
   */
  @computed
  get paymentMethods() {
    return this.adapter.paymentMethods()
  }

  /**
   * Whether we can add another method.
   */
  @computed
  get canAddPaymentMethod() {
    return this.adapter.canAddMethod()
  }

  /**
   * Selects a Billing Method.
   */
  selectMethod = task.resolved(async (method: BillingMethod) => {
    await this.adapter.onSave?.(method)
    this.onSelected(method)
  })

  /**
   * Fetches the billing info.
   */
  prepare = task(async (initialSelectedPaymentMethod: BillingMethod | null) => {
    this.setMethod(initialSelectedPaymentMethod)
    await this.adapter.fetchBillingProfile()
    // Show the new payment method modal immediately if there
    // are no payment methods.
    if (this.paymentMethods.length === 0) {
      this.newPaymentMethod().then()
    }
  })

  /**
   * Starts the New Payment Method flow.
   */
  newPaymentMethod = task.resolved(async () => {
    // We use the modal flow here which needs a cancellation token.
    const cts = cancelOnDeactivate(this.adapter.active$)
    const method = await this.adapter.newPaymentMethod(cts.token)
    cts.dispose()

    if (!method) {
      return
    }

    await this.selectMethod(method)
  })

  /**
   * Called when a billing method is selected.
   * @param method
   */
  private onSelected(method: BillingMethod) {
    this.setMethod(method)
    this.onPaymentMethodSelected.next(method)
  }

  /**
   * Sets the method.
   *
   * @param method
   * @private
   */
  @action.bound
  private setMethod(method: BillingMethod | null) {
    this.selectedPaymentMethod = method
  }
}

/**
 * Ports for the selector VM.
 */
export interface PaymentMethodSelectorViewModelPorts {
  /**
   * An observable that emits `true` while the caller is active, `false`
   * when not.
   */
  readonly active$: Observable<boolean>
  /**
   * Called when a method is selected. Allows the
   * consumer to save a selected method before the selector is closed.
   */
  readonly onSave: OnSaveDelegate | null
  /**
   * Fetches the billing profile.
   */
  fetchBillingProfile(): Promise<unknown>
  /**
   * Reactive getter for the payment methods.
   */
  paymentMethods(): BillingMethod[]
  /**
   * Whether adding a new method is allowed.
   */
  canAddMethod(): boolean
  /**
   * New Payment Method flow.
   *
   * @param ct
   */
  newPaymentMethod(ct: CancellationToken): Promise<BillingMethod | null>
}
